export const MAX_CART_ATTENDEES = 10;

export type TicketCartItem = {
  ticketId: string;
  name: string;
  price: number;
  showPlatformFee?: boolean;
  quantity: number;
};

export type TicketCartSlot = {
  ticketId: string;
  name: string;
  price: number;
  showPlatformFee?: boolean;
};

export type TicketOrderLimitSource = {
  minPerOrder?: number | null;
  maxPerOrder?: number | null;
  quantity?: number | null;
};

function parseExplicitMaxPerOrder(ticket: TicketOrderLimitSource): number | null {
  if (ticket.maxPerOrder == null || String(ticket.maxPerOrder).trim() === '') return null;
  const value = Math.floor(Number(ticket.maxPerOrder));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Ingresso com mínimo/máximo por compra > 1 (configurado no painel). */
export function hasExplicitMultiPurchase(ticket: TicketOrderLimitSource): boolean {
  const min = Math.max(1, Math.floor(Number(ticket.minPerOrder) || 1));
  const max = parseExplicitMaxPerOrder(ticket);
  return min > 1 || (max != null && max > 1);
}

/** Limites por tipo de ingresso na seleção (respeita estoque e teto do pedido). */
export function getTicketOrderLimits(ticket: TicketOrderLimitSource): { min: number; max: number } {
  const stock = Math.max(0, Math.floor(Number(ticket.quantity) || 0));
  let min = Math.max(1, Math.floor(Number(ticket.minPerOrder) || 1));
  const configuredMax = parseExplicitMaxPerOrder(ticket) ?? MAX_CART_ATTENDEES;
  let max = Math.min(
    MAX_CART_ATTENDEES,
    Number.isFinite(configuredMax) && configuredMax > 0 ? configuredMax : MAX_CART_ATTENDEES,
    stock > 0 ? stock : MAX_CART_ATTENDEES
  );
  if (stock > 0) {
    min = Math.min(min, stock);
    max = Math.max(min, Math.min(max, stock));
  } else {
    max = 0;
    min = 1;
  }
  return { min, max };
}

/**
 * Quantidade selecionável na inscrição.
 * Sem “várias pessoas no pedido”, só libera > 1 se o ingresso tiver máximo/mínimo explícito > 1.
 */
export function getRegistrationQuantityLimits(
  ticket: TicketOrderLimitSource,
  allowGroupRegistration?: boolean
): { min: number; max: number } {
  const limits = getTicketOrderLimits(ticket);
  if (limits.max <= 0) return { min: 1, max: 0 };
  if (allowGroupRegistration || hasExplicitMultiPurchase(ticket)) {
    return { min: limits.min, max: Math.min(limits.max, MAX_CART_ATTENDEES) };
  }
  return { min: 1, max: 1 };
}

const STORAGE_PREFIX = 'ticket-cart:';

export function cartStorageKey(eventSlug: string): string {
  return `${STORAGE_PREFIX}${eventSlug}`;
}

export function cartPersonCount(items: TicketCartItem[]): number {
  return items.reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0);
}

export function expandCartToSlots(items: TicketCartItem[]): TicketCartSlot[] {
  const slots: TicketCartSlot[] = [];
  items.forEach((item) => {
    const qty = Math.max(0, Math.floor(Number(item.quantity) || 0));
    for (let i = 0; i < qty; i += 1) {
      slots.push({
        ticketId: item.ticketId,
        name: item.name,
        price: item.price,
        showPlatformFee: item.showPlatformFee,
      });
    }
  });
  return slots;
}

export function loadTicketCart(eventSlug: string): TicketCartItem[] {
  if (!eventSlug || typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(cartStorageKey(eventSlug));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        ticketId: String(item.ticketId || ''),
        name: String(item.name || ''),
        price: Number(item.price) || 0,
        showPlatformFee: !!item.showPlatformFee,
        quantity: Math.max(0, Math.floor(Number(item.quantity) || 0)),
      }))
      .filter((item) => item.ticketId && item.quantity > 0);
  } catch {
    return [];
  }
}

export function saveTicketCart(eventSlug: string, items: TicketCartItem[]): TicketCartItem[] {
  const normalized = items.filter((item) => item.ticketId && item.quantity > 0);
  if (typeof sessionStorage !== 'undefined') {
    if (normalized.length === 0) {
      sessionStorage.removeItem(cartStorageKey(eventSlug));
    } else {
      sessionStorage.setItem(cartStorageKey(eventSlug), JSON.stringify(normalized));
    }
  }
  return normalized;
}

export function clearTicketCart(eventSlug: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(cartStorageKey(eventSlug));
}

export function addTicketToCart(
  eventSlug: string,
  ticket: Omit<TicketCartItem, 'quantity'>,
  quantity = 1
): { items: TicketCartItem[]; added: number; error?: string } {
  const current = loadTicketCart(eventSlug);
  const remaining = MAX_CART_ATTENDEES - cartPersonCount(current);
  if (remaining <= 0) {
    return { items: current, added: 0, error: `Máximo de ${MAX_CART_ATTENDEES} pessoas por pedido` };
  }
  const toAdd = Math.min(Math.max(1, Math.floor(quantity)), remaining);
  const existing = current.find((item) => item.ticketId === ticket.ticketId);
  if (existing) {
    existing.quantity += toAdd;
    existing.name = ticket.name;
    existing.price = ticket.price;
    existing.showPlatformFee = ticket.showPlatformFee;
  } else {
    current.push({ ...ticket, quantity: toAdd });
  }
  return { items: saveTicketCart(eventSlug, current), added: toAdd };
}

export function setTicketCartQuantity(
  eventSlug: string,
  ticketId: string,
  quantity: number
): { items: TicketCartItem[]; error?: string } {
  const current = loadTicketCart(eventSlug);
  const others = cartPersonCount(current.filter((item) => item.ticketId !== ticketId));
  const nextQty = Math.max(0, Math.floor(quantity));
  if (others + nextQty > MAX_CART_ATTENDEES) {
    return { items: current, error: `Máximo de ${MAX_CART_ATTENDEES} pessoas por pedido` };
  }
  const next = current
    .map((item) => (item.ticketId === ticketId ? { ...item, quantity: nextQty } : item))
    .filter((item) => item.quantity > 0);
  return { items: saveTicketCart(eventSlug, next) };
}

export function removeTicketFromCart(eventSlug: string, ticketId: string): TicketCartItem[] {
  return saveTicketCart(
    eventSlug,
    loadTicketCart(eventSlug).filter((item) => item.ticketId !== ticketId)
  );
}
